/**
 * 1.3.1, the media portal's extras: the shared contract of the four build streams (docs/dev/portal-1.3.1-build.md),
 * beside portal.ts (1.3.0's, which keeps the error codes, the email kinds and their placeholders, and the string
 * catalogue). The workflow steps' configs are in automation.ts (assign_for_delivery, the delivery_accepted check).
 *
 *   Q1  quotas and usage, email text overrides, logo and accent, the company activity view
 *   Q2  reminders, expiry, the Media portal statistics
 *   Q3  the DMZ split (PRISM_API_ROLE)
 *   Q4  the workflow steps
 *
 * The small pure helpers here (placeholders, contrast, quota checks) are shared by the API and the web app's
 * previews, so both say the same thing.
 */
import { z } from 'zod';
import { PORTAL_EMAIL_KINDS, PORTAL_EMAIL_PLACEHOLDERS, type PortalEmailKind, type PortalEmailTextDTO } from './portal.ts';
import { STATISTICS_RANGES, type StatisticsRange } from './statistics.ts';

/* ================================================================ Q1: quotas and usage */

/** Staff are warned in System status (and the usage bar turns yellow) from this share of a quota. */
export const PORTAL_QUOTA_WARNING_RATIO = 0.8;
export const PORTAL_QUOTA_KINDS = ['month', 'pending'] as const;
export type PortalQuotaKind = (typeof PORTAL_QUOTA_KINDS)[number];

/**
 * A company's use of the intake location (Q1, portal/quota.ts): the bytes of its submissions' files there.
 *   month    files uploaded in the current calendar month (UTC), whatever became of them;
 *   pending  files of submissions not decided yet (draft, waiting, needs_changes).
 * A limit of null: no quota of that kind.
 */
export interface PortalQuotaUsageDTO {
  companyId: string;
  /** 'YYYY-MM' (UTC) the month figure is for. */
  month: string;
  monthBytes: number;
  pendingBytes: number;
  quotaBytesMonth: number | null;
  quotaBytesPending: number | null;
  /** used / limit, 0–1 and above; null without a quota. */
  monthRatio: number | null;
  pendingRatio: number | null;
  /** Any ratio at or above PORTAL_QUOTA_WARNING_RATIO. */
  warning: boolean;
}
/** GET /api/v1/admin/portal/usage: every company's usage (the Companies & users bars). */
export interface PortalQuotaUsageListDTO { items: PortalQuotaUsageDTO[] }

/** The details of a 507 PORTAL_QUOTA: which quota, what is used and the limit, and the refused upload's size. */
export interface PortalQuotaRefusal { which: PortalQuotaKind; usedBytes: number; limitBytes: number; sizeBytes: number }

/** Whether an upload of sizeBytes fits; the first quota it would pass (month before pending), or null. Pure. */
export function quotaRefusal(usage: Pick<PortalQuotaUsageDTO, 'monthBytes' | 'pendingBytes' | 'quotaBytesMonth' | 'quotaBytesPending'>, sizeBytes: number): PortalQuotaRefusal | null {
  if (usage.quotaBytesMonth !== null && usage.monthBytes + sizeBytes > usage.quotaBytesMonth) {
    return { which: 'month', usedBytes: usage.monthBytes, limitBytes: usage.quotaBytesMonth, sizeBytes };
  }
  if (usage.quotaBytesPending !== null && usage.pendingBytes + sizeBytes > usage.quotaBytesPending) {
    return { which: 'pending', usedBytes: usage.pendingBytes, limitBytes: usage.quotaBytesPending, sizeBytes };
  }
  return null;
}

/* ================================================================ Q1: email text overrides */

/** The {placeholders} a text uses (each once, in order of first use). */
export function placeholdersIn(text: string): string[] {
  return [...new Set([...text.matchAll(/\{(\w+)\}/g)].map((m) => m[1]!))];
}

/** The {placeholders} of a text that its kind does not have (an override with any is refused: 422 EMAIL_TEXT_PLACEHOLDER). */
export function unknownPlaceholders(kind: PortalEmailKind, text: string): string[] {
  const known = PORTAL_EMAIL_PLACEHOLDERS[kind];
  return placeholdersIn(text).filter((p) => !known.includes(p));
}

/** Fills {placeholders} as t() does: a placeholder without a value stays as written. */
export function fillPlaceholders(text: string, vars: Record<string, string | number>): string {
  return text.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}

/** Example values for the preview of every placeholder (Q1's editor and the API's preview route). */
export const PORTAL_EMAIL_SAMPLE_VARS: Record<string, string> = {
  name: 'Alex Supplier', company: 'Northern Lights Productions', portalName: 'Media portal', link: 'https://portal.example.com/…',
  expires: '31 October 2026', title: 'Evening news opener', assetCode: 'ASSET_2026_123', due: '31 October 2026 at 16:00 (Europe/Helsinki)',
  items: 'Media, Manuscript (PDF)', contact: 'Sam Producer <sam@example.com>', item: 'Manuscript', reason: 'The file is the wrong cut.',
  comment: 'Please also send the music list.', result: 'passed with warnings', errors: '0', warnings: '2',
};

/** PUT /api/v1/admin/portal/email-texts/:kind (Q1): the override; refused with any placeholder the kind lacks. */
export const PortalEmailTextPut = z.object({
  subject: z.string().trim().min(1).max(300),
  body: z.string().trim().min(1).max(20000),
});
export type PortalEmailTextPut = z.infer<typeof PortalEmailTextPut>;
/** POST /api/v1/admin/portal/email-texts/:kind/preview: the text (saved or not) rendered with the sample values. */
export const PortalEmailTextPreviewBody = PortalEmailTextPut.partial();
export type PortalEmailTextPreviewBody = z.infer<typeof PortalEmailTextPreviewBody>;
export interface PortalEmailPreviewDTO { kind: PortalEmailKind; subject: string; text: string; html: string; unknown: string[] }
/** GET /api/v1/admin/portal/email-texts: every supplier kind, its text now (override or catalogue), and its placeholders. */
export interface PortalEmailTextsDTO { items: PortalEmailTextDTO[] }
export const PORTAL_EMAIL_TEXT_KINDS = PORTAL_EMAIL_KINDS;

/* ================================================================ Q1: logo and accent */

export const PORTAL_LOGO_TYPES = ['image/png', 'image/svg+xml'] as const;
export type PortalLogoType = (typeof PORTAL_LOGO_TYPES)[number];
/** The largest logo accepted (PUT /api/v1/admin/portal/logo; 422 LOGO_INVALID above it). */
export const PORTAL_LOGO_MAX_BYTES = 512 * 1024;
/** The logo as Branding shows it; null without one. url is on the portal's host (GET /api/v1/portal/logo?v=…). */
export interface PortalLogoDTO { url: string; mime: PortalLogoType; bytes: number; updatedAt: string }

/** An accent colour must reach this contrast ratio against white (the portal's buttons carry white text on it). */
export const PORTAL_ACCENT_MIN_CONTRAST = 4.5;

/** WCAG relative luminance of #rrggbb. */
export function relativeLuminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) throw new Error(`Not a #rrggbb colour: ${hex}`);
  const [r, g, b] = [m[1]!, m[2]!, m[3]!].map((h) => {
    const c = parseInt(h, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

/** WCAG contrast ratio of two #rrggbb colours (1–21). */
export function contrastRatio(a: string, b: string): number {
  const [x, y] = [relativeLuminance(a), relativeLuminance(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
}

/** Whether an accent colour may be saved (PATCH settings accentColor; 422 with the ratio otherwise). */
export function accentContrastOk(hex: string): { ok: boolean; ratio: number } {
  const ratio = contrastRatio(hex, '#ffffff');
  return { ok: ratio >= PORTAL_ACCENT_MIN_CONTRAST, ratio: Math.round(ratio * 100) / 100 };
}

/* ================================================================ Q1: the company activity view */

/** Companies & users → a company → Activity, filtered by kind (action patterns in PORTAL_ACTIVITY_ACTIONS). */
export const PORTAL_ACTIVITY_KINDS = ['all', 'sign_ins', 'uploads', 'decisions', 'comments'] as const;
export type PortalActivityKind = (typeof PORTAL_ACTIVITY_KINDS)[number];
/** The audit actions each kind shows (LIKE patterns); 'all' is every portal.* row of the company's users and every intake.* row on its submissions. */
export const PORTAL_ACTIVITY_ACTIONS: Record<Exclude<PortalActivityKind, 'all'>, readonly string[]> = {
  sign_ins: ['portal.sign_in', 'portal.sign_in_failed', 'portal.sign_out', 'portal.two_step.%'],
  uploads: ['portal.upload', 'portal.submit', 'portal.withdraw'],
  decisions: ['intake.item.decide', 'intake.accept', 'intake.reject'],
  comments: ['portal.note', 'intake.note'],
};

/** GET /api/v1/admin/portal/companies/:id/activity?limit=&before=&kind= (and ?format=csv for the export). */
export const PortalActivityQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** The previous page's nextCursor (the asset activity log's (at, id) cursor). */
  before: z.string().max(200).optional(),
  kind: z.enum(PORTAL_ACTIVITY_KINDS).default('all'),
  format: z.enum(['json', 'csv']).default('json'),
});
export type PortalActivityQuery = z.infer<typeof PortalActivityQuery>;

export interface PortalActivityRowDTO {
  /** audit_events.id. */
  id: string;
  at: string;
  action: string;
  outcome: 'success' | 'denied' | 'error';
  /** portal: one of the company's users; user: a member (decisions, notes). */
  actorKind: 'portal' | 'user';
  actorId: string | null;
  actor: string | null;
  /** The submission the row is about, when it is about one. */
  submissionId: string | null;
  /** One line for the table and the CSV (filename, decision, reason …). */
  summary: string;
  detail: Record<string, unknown>;
}
export interface PortalActivityPageDTO { items: PortalActivityRowDTO[]; nextCursor: string | null }
/** The CSV export's columns, in order. */
export const PORTAL_ACTIVITY_CSV_COLUMNS = ['at', 'action', 'outcome', 'actor_kind', 'actor', 'submission_id', 'summary'] as const;

/* ================================================================ Q2: reminders and expiry */

/** A reminder sent (portal_reminders_sent, 0093). */
export interface PortalReminderDTO { assignmentId: string; offsetDays: number; dueAt: string; sentAt: string; emails: number }
/** GET /api/v1/admin/portal/reminders?limit= (portal.manage): a reminder sent, with what it was for; newest first. */
export interface PortalReminderRowDTO extends PortalReminderDTO { assetCode: string; title: string; company: string }

/**
 * The reminders due now for an assignment (Q2's loop): every offset whose moment (due − offset days) has passed and
 * that was not sent for this due date, while the due date itself is still ahead. Pure; the loop also checks the state.
 */
export function remindersDue(dueAt: string, offsetsDays: readonly number[], sent: ReadonlyArray<{ offsetDays: number; dueAt: string }>, now: Date): number[] {
  const due = Date.parse(dueAt);
  if (!(due > now.getTime())) return [];
  const done = new Set(sent.filter((s) => Date.parse(s.dueAt) === due).map((s) => s.offsetDays));
  // Only the nearest passed offset: a reminder 3 days before that was missed is not sent beside the 1-day one.
  const passed = [...new Set(offsetsDays)].filter((d) => due - d * 86_400_000 <= now.getTime()).sort((a, b) => a - b);
  const nearest = passed[0];
  return nearest !== undefined && !done.has(nearest) ? [nearest] : [];
}

/** When an assignment expires: expiry_days after its due date, or never (no due date, or no expiry set). Pure. */
export function assignmentExpiresAt(dueAt: string | null, expiryDays: number | null): string | null {
  if (!dueAt || !expiryDays) return null;
  return new Date(Date.parse(dueAt) + expiryDays * 86_400_000).toISOString();
}

/* ================================================================ Q2: the Media portal statistics */

/** GET /api/v1/statistics/portal?range= (admin, as the rest of Statistics). */
export const PortalStatisticsQuery = z.object({ range: z.enum(STATISTICS_RANGES).default('12m') });
export interface PortalStatisticsRowDTO {
  /** 'YYYY-MM'. */
  month: string;
  companyId: string;
  uploads: number;
  bytes: number;
  /** Submissions that arrived (submitted). */
  submissions: number;
  accepted: number;
  rejected: number;
  /** accepted / (accepted + rejected); null with no decisions. */
  acceptanceRate: number | null;
  /** The median time from arrival to decision, seconds; null with no decisions. */
  medianDecisionSeconds: number | null;
}
export interface PortalStatisticsDTO {
  range: StatisticsRange;
  /** The months shown, oldest first ('YYYY-MM'). */
  months: string[];
  companies: Array<{ id: string; name: string; disabled: boolean }>;
  /** One row per month and company with any activity. */
  rows: PortalStatisticsRowDTO[];
  /** Every company together, per month (medians of all the month's decisions, not of the companies' medians). */
  totals: Array<Omit<PortalStatisticsRowDTO, 'companyId'>>;
  /** The first day portal_daily has (1.3.1 started counting); null before any. */
  trackedSince: string | null;
  /**
   * The whole range's figures, every company together and per company, with the median over all the range's
   * decisions (not a median of the months'): the tiles.
   */
  summary: { all: PortalRangeFiguresDTO; companies: Array<PortalRangeFiguresDTO & { companyId: string }> };
}
/** A range's figures (PortalStatisticsDTO.summary): a row without its month and company. */
export type PortalRangeFiguresDTO = Omit<PortalStatisticsRowDTO, 'month' | 'companyId'>;

/** Median of a list of numbers (null when empty). Pure; Q2's API and tests use it. */
export function medianOf(values: readonly number[]): number | null {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

/* ================================================================ Q3: the DMZ split */

/**
 * PRISM_API_ROLE: all (every route and the background work, the default), portal (only the portal's), and since
 * 1.8.3 serve (every route, no background work) and jobs (the background work, /healthz, /readyz and /metrics only).
 * services/api/src/modules/portal/api-role.ts describes each (describeApiRole).
 */
export const API_ROLES = ['all', 'serve', 'jobs', 'portal'] as const;
export type ApiRole = (typeof API_ROLES)[number];
